import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import FastlyImage from '@components/common/fastly-image'
import ContentCard from '@components/course-landing-page/content-card'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { CSS } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['wd'].trialCourseQuery}`

export const CSSSeo = formatSeoConfig({
  title: 'CSS for Kids: Online Web Design Classes',
  description:
    'CSS for kids taught by expert instructors. Transform pages into vibrant, interactive designs with a structured curriculum at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/courses/css-for-kids/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-dark-v2.jpg',
})
const Css = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...CSSSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[CSS].map(({ acceptedAnswerText, questionName }) => ({
          acceptedAnswerText: acceptedAnswerText,
          questionName: questionName,
        }))}
      />
      <CourseJsonLd
        courseName="Learn CSS with the Top AI & Coding Course Instructors for Kids and Teens"
        description="Codingal offers online CSS classes for kids and teens, designed to make web design both fun and educational. Learning CSS is a creative journey that allows children to transform plain webpages into vibrant, interactive designs. As part of our web development program for young learners, we offer a six-lesson class where students explore background effects, text styling, and advanced techniques that make their web creations more powerful. They also learn how to structure pages using HTML and enhance their appearance with CSS, gaining a strong foundation in web development. For children, CSS is like a digital paintbrush. It helps them make websites colorful, attractive, and responsive. With these skills, students can change fonts, adjust spacing, add animations, and control how their webpages look on different devices. This process introduces them to interactive web design for kids, encourages creative problem-solving, and helps them develop design skills that prepare them to be future web designers. It's truly the best way to learn CSS for kids while having fun with coding."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={CSS}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={CSS} />
          <CourseFeatures
            courseTitle={CSS}
            title={<Trans>An engaging CSS programming lessons for kids</Trans>}
          />
          <ClassCurriculum
            courseName={CSS}
            title="Our CSS Class Curriculum"
            description={
              <Trans>
                The CSS module builds upon students’ knowledge of HTML and
                introduces the power of visual design through coding. Starting
                with background effects and text styling, students explore
                advanced selectors and layout techniques using the Box Model.
                They also learn positioning strategies to control how elements
                appear and interact on the page. Each lesson is paired with
                hands-on practice, helping students transform simple HTML
                structures into professionally styled web pages.
              </Trans>
            }
          />
          <Activities courseName={CSS} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                <p>
                  Our instructors bring deep expertise in Computer Science and
                  complete rigorous training before they teach; reflected in our
                  exceptional Net Promoter Score (NPS) of 86. Students and
                  parents consistently rate our teachers 4.9 out of 5, and our
                  curriculum earns a 4.8 out of 5. We’re proud to have a faculty
                  that’s 91% female and 9% male, creating an inclusive,
                  encouraging learning environment. Each tutor is trained to
                  understand individual student needs and deliver personalized
                  support; even in group settings. Plus, students have 24/7
                  access to expert tutors for instant doubt resolution, anytime
                  they need help. This makes learning HTML and CSS a truly
                  supported experience, especially through our virtual CSS
                  lessons that connect kids with top educators worldwide.
                </p>
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals of
                Website Development
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we create the perfect environment for kids and
                  teens to master CSS from the ground up. Our thoughtfully
                  designed curriculum combines real-world styling tools,
                  creative design projects, and interactive learning to make the
                  process of understanding web design both comprehensive and
                  fun. For those asking how to learn HTML and CSS, this is your
                  answer.
                </span>
              </Trans>
            }
            courseName={CSS}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                <p>
                  At Codingal, your child masters CSS through a
                  STEM.org–accredited program built on proven educational
                  frameworks like Bloom’s Taxonomy, BIDE, and STEAM. Our
                  curriculum is aligned with Common Core, state standards, AP
                  Computer Science goals, and the K–12 Computer Science
                  Framework; ensuring learning that’s both age-appropriate and
                  college-ready. Through this structured approach, kids
                  experience the benefits of children’s CSS programming in a
                  supported environment.
                </p>
                <p>
                  Through hands-on, project-based learning, students create
                  real-world apps like temperature converters and scientific
                  calculators, applying math, science, and engineering logic to
                  solve everyday problems. These activities not only build
                  technical confidence but also encourage styling websites for
                  kids in creative, practical ways.
                </p>
                <p>
                  Backed by STEM.org’s rigorous audits, every CSS lesson lays a
                  strong, future-proof foundation in STEM. This helps to boost
                  creativity in kids, develop critical thinking, and inspire
                  them to pursue future opportunities in technology and design.
                </p>
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your CSS certifications"
            description={
              <>
                Upon successful completion of the CSS module, students receive
                the CSS Developer Certificate. This certificate recognizes their
                ability to style and design web pages using CSS and demonstrates
                their proficiency in applying visual design principles through
                code. It is a valuable addition to their academic portfolio and
                showcases their achievement in front-end development for kids
                fundamentals. Students can proudly include this certificate in
                their school presentations, personal websites, or future college
                applications, highlighting their skills in web design for kids
                and creative problem-solving, while showing parents why Codingal
                offers one of the best CSS classes for kids.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our CSS classes are appropriate for middle school & high school students"
            description={
              <>
                At Codingal, our CSS curriculum takes middle and high school
                students from writing their first style rule, like changing text
                color or background, to designing visually polished, responsive
                web pages. We begin with beginner-friendly lessons that
                introduce core styling concepts such as color, fonts, spacing,
                and borders through interactive, hands-on projects. As students
                progress, they explore more advanced topics like the box model,
                positioning, pseudo-classes, and responsive layouts, ensuring a
                smooth and steady learning curve. With every project, students
                enhance their design thinking, develop a strong understanding of
                how websites look and feel, and gain the confidence to create
                beautiful, well-structured web pages. This is how to learn CSS
                for kids effectively, blending interactive web design for kids
                with hands-on practice and preparing them to become a future web
                designer.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['wd', 'cgm']} />

          <ClassCurriculum
            courseName={CSS}
            title="About CSS"
            description={
              <div className="space-y-3">
                <h2 className="font-600 text-lg">What Does CSS Stand For?</h2>
                <div className="my-4">
                  <FastlyImage
                    src={`/images/courses/css/house-analogy.jpg`}
                    alt="House analogy: HTML (plain) vs. CSS (decorated) house"
                    width={512}
                    height={341}
                  />
                </div>
                <p>
                  CSS stands for Cascading Style Sheets. While HTML gives a
                  webpage its basic structure, CSS is the code used to style it.
                  You can think of it as the paint, furniture, and decorations
                  for a house built with HTML. This is fundamental for anyone
                  learning HTML and CSS for kids.
                </p>
                <h2 className="font-600 text-lg mt-3">
                  An Easy Way to Understand CSS
                </h2>
                <p>
                  Here’s a simple analogy to help you understand the difference
                  between HTML and CSS. Imagine you are drawing a superhero. The
                  first step is to sketch the basic outline of the character
                  i.e. the head, body, arms, and legs. That simple sketch is
                  your HTML. It provides the essential structure, but it’s
                  plain. Now, you start adding the details: you color the
                  costume, add a logo, draw a cape, and style the hair. All of
                  those design choices are your CSS. It’s what gives the
                  character personality and makes them look unique and powerful.
                  This is exactly how kids learn the difference between HTML and
                  CSS in our kids CSS classes.
                </p>
                <h2 className="font-600 text-lg mt-3">
                  What Kids Learn with CSS?
                </h2>
                <p>
                  When kids learn CSS, they are learning how to be digital
                  designers. It gives them a strong sense of design and
                  aesthetics. For those learning CSS for kids, understanding
                  these design principles is crucial. They discover how to
                  choose colors and fonts, create layouts that look good on any
                  device, add backgrounds and images, and even use animations
                  for interactivity. CSS makes visual coding for kids exciting,
                  turning their work into digital art for kids.
                </p>
                <p>Here are a few things they can do with CSS:</p>
                <ol className="space-y-4">
                  <li className="space-y-2">
                    <span>
                      Choose Colors and Fonts: Kids can change the color, size,
                      and style of any text on the page.
                    </span>
                    <div>
                      <FastlyImage
                        src={`/images/courses/css/hello.jpg`}
                        alt="Choose Colors and Fonts"
                        width={512}
                        height={341}
                      />
                    </div>
                  </li>
                  <li className="space-y-2">
                    <span>
                      Create Layouts: They can position elements exactly where
                      they want them. This helps them build layouts that look
                      good on any screen size. This is key for learning CSS
                      Flexbox and CSS Grid.
                    </span>
                    <div>
                      <FastlyImage
                        src={`/images/courses/css/layouts.jpg`}
                        alt="Create Layouts"
                        width={512}
                        height={341}
                      />
                    </div>
                  </li>
                  <li className="space-y-2">
                    <span>
                      Add Backgrounds: It&lsquo;s easy to add background colors
                      or images to make a site more interesting.
                    </span>
                    <div>
                      <FastlyImage
                        src={`/images/courses/css/background.jpg`}
                        alt="Add Backgrounds"
                        width={512}
                        height={341}
                      />
                    </div>
                  </li>
                  <li className="space-y-2">
                    <span>
                      Use Animations: CSS can create smooth visual effects and
                      interactive touches, like making a button glow when you
                      hover over it.
                    </span>
                    <div>
                      <FastlyImage
                        src={`/images/courses/css/animations.jpg`}
                        alt="Use Animations"
                        width={512}
                        height={341}
                      />
                    </div>
                  </li>
                </ol>
              </div>
            }
          />
          <Faq courseName={CSS} title="Frequently Asked Questions" />
          <CodingalProductBlog urlQuery={urlQuery} isCoursePage />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={CSS}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="CSS for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Css
